"use client";

import { JSX, CSSProperties } from "react";
import { useTranslations } from "next-intl";
import type { BreakoutTheme } from "@client/utils/breakoutThemes";

type BreakoutAssignedPromptBarProps = {
  theme: BreakoutTheme;
  onJoinBreakout: () => void;
};

export function BreakoutAssignedPromptBar({ theme, onJoinBreakout }: BreakoutAssignedPromptBarProps): JSX.Element {
  const tBreakout = useTranslations("breakoutRooms");
  const { color, name } = theme;

  return (
    <div
      className="mx-2 flex w-full max-w-4xl justify-center sm:mx-4"
      role="status"
      style={{ "--room-color": color } as CSSProperties}
    >
      <div
        className="flex flex-wrap items-center justify-center gap-3 rounded-xl px-4 py-2.5 backdrop-blur-md sm:gap-4 sm:px-5"
        style={{
          backgroundColor: `${color}1A`,
          border: `1px solid ${color}66`,
          boxShadow: `0 4px 24px -2px ${color}33`
        }}
      >
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-600">
            {tBreakout("assignedPromptLabel")}
          </span>
          <span className="text-sm font-bold tracking-wide" style={{ color }}>
            {name}
          </span>
        </div>
        <button
          type="button"
          onClick={onJoinBreakout}
          className="cursor-pointer rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 active:scale-95"
          style={{ backgroundColor: color }}
        >
          {tBreakout("moveToBreakout")}
        </button>
      </div>
    </div>
  );
}

type BreakoutStatusBarProps = {
  theme: BreakoutTheme;
  showReturn: boolean;
  onReturnToMain: () => void;
};

export function BreakoutStatusBar({ theme, showReturn, onReturnToMain }: BreakoutStatusBarProps): JSX.Element {
  const tBreakout = useTranslations("breakoutRooms");
  const { color, name } = theme;

  return (
    <div
      className="mx-2 flex w-full max-w-4xl justify-center sm:mx-4"
      role="status"
      style={{ "--room-color": color } as CSSProperties}
    >
      <div
        className="flex flex-wrap items-center justify-center gap-3 rounded-xl px-4 py-2.5 backdrop-blur-md sm:gap-4 sm:px-5"
        style={{
          backgroundColor: `${color}1A`,
          border: `1px solid ${color}66`,
          boxShadow: `0 4px 24px -2px ${color}33`
        }}
      >
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
          <span
            className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full"
            style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }}
            aria-hidden
          />
          <span className="text-xs font-medium uppercase tracking-wider text-gray-300">
            {tBreakout("breakoutLabel")}
          </span>
          <span className="text-sm font-bold tracking-wide" style={{ color }}>
            {name}
          </span>
        </div>
        {showReturn ? (
          <button
            type="button"
            onClick={onReturnToMain}
            className="cursor-pointer rounded-lg bg-white/10 px-3 py-1 text-xs font-semibold text-white transition hover:bg-white/20 active:scale-95"
            style={{ border: `1px solid ${color}40` }}
          >
            {tBreakout("toolbarReturnMain")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
